// src/pages/SutraPage.jsx

import React, { useState } from 'react';
import { Link } from 'react-router-dom'; // Import Link for the bottom button
import './SutraPage.css';

// --- IMPORTS ---
// Video
import sutraVideoSource from '../assets/videos/sutra-promo.mp4'; 

// Images
import editorImg from '../assets/images/sutra-editor.png';
import workflowImg from '../assets/images/sutra-workflow.png';
import noBoredomLogo from '../assets/images/no-boredom.png'; // Reusing from upcoming page
import sdlcLogo from '../assets/images/sdlc-logo.png';       // NEW LOGO

const SutraPage = () => {
  
  // --- STATE MANAGEMENT ---
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState('idle');

  // --- HANDLERS ---
  const handleOverlayClick = (e) => {
    if (e.target.className === 'modal-overlay') {
      setIsFormOpen(false);
      setIsEmailModalOpen(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    setEmailStatus('submitting');
    setTimeout(() => {
      setEmailStatus('success');
    }, 1500);
  };

  // Smooth scroll to features section
  const scrollToFeatures = () => {
    const element = document.getElementById('sutra-features-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="sutra-page-container">
      
      {/* --- HERO SECTION --- */}
      <div className="sutra-content">
        <h1 className="sutra-title">SUTRA AI</h1>
        <h2 className="sutra-tagline">Stop Building Levels. Start Shipping Worlds.</h2>
        
        {/* --- VIDEO TRIAD LAYOUT --- */}
        <div className="video-triad-container">
          
          {/* Left Logo */}
          <div className="side-logo-wrapper left-logo">
            <img src={noBoredomLogo} alt="No Boredom" className="side-logo-img pulse-anim" />
          </div>

          {/* Center Video */}
          <div className="video-wrapper">
            <video 
              className="sutra-video" 
              controls 
              autoPlay 
              muted 
              loop 
              playsInline
            >
              <source src={sutraVideoSource} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>

          {/* Right Logo */}
          <div className="side-logo-wrapper right-logo">
            <img src={sdlcLogo} alt="SDLC Revolution" className="side-logo-img" />
          </div>

        </div>
        
        <p className="sutra-description">
          Experience the next generation of AI integration. 
          Runtime generation, neural verification, and infinite adaptability.
        </p>

        {/* --- ACTION BUTTONS (Row of 3) --- */}
        <div className="sutra-actions">
          {/* 1. Wishlist */}
          <button onClick={() => setIsEmailModalOpen(true)} className="action-btn wishlist-btn">
            <span>♥</span> Wishlist Now
          </button>

          {/* 2. Join Beta */}
          <button onClick={() => setIsFormOpen(true)} className="action-btn form-btn">
            <span>📝</span> Join Beta
          </button>

          {/* 3. Features (Scroll Down) */}
          <button onClick={scrollToFeatures} className="action-btn features-btn">
            <span>⇩</span> Features
          </button>
        </div>
      </div>

      {/* --- FEATURE EXPLANATION SECTION (ZigZag) --- */}
      {/* ID added for scrolling */}
      <div id="sutra-features-section" className="sutra-features">
        
        {/* Row 1: Image Left, Text Right */}
        <div className="feature-row">
          <div className="feature-image-wrapper">
            <img src={editorImg} alt="Sutra AI Director Interface" className="feature-img" />
          </div>
          <div className="feature-text">
            <h2>The AI Director</h2>
            <p>
              Harness the power of <strong>Runtime Generation</strong>. Our proprietary LTL Verifier 
              ensures logic consistency while Neural Weights adapt the environment complexity 
              in real-time based on player skill.
            </p>
            <ul className="feature-list">
              <li>Dynamic Node Graph Visualization</li>
              <li>Real-time Entropy & Complexity Control</li>
              <li>Seamless Neural Weight Baking</li>
            </ul>
          </div>
        </div>

        {/* Row 2: Text Left, Image Right */}
        <div className="feature-row reverse">
          <div className="feature-text">
            <h2>Compressed Workflow</h2>
            <p>
              Forget the bottlenecks of the traditional "Build & Release" cycle. 
              Sutra AI shifts the paradigm by moving generation to the 
              <strong> Player Device</strong>.
            </p>
            <p>
              While traditional workflows stop at release, Sutra creates an infinite loop 
              of content generation, ensuring no two playthroughs are ever the same.
            </p>
          </div>
          <div className="feature-image-wrapper">
            <img src={workflowImg} alt="Workflow Comparison" className="feature-img" />
          </div>
        </div>

      </div>

      {/* --- BOTTOM CTA: SHOWCASE GAMES --- */}
      <div className="bottom-cta-container">
        <Link to="/upcoming" className="showcase-games-btn">
          Showcase Games <span>&rarr;</span>
        </Link>
      </div>

      {/* --- MODALS --- */}
      {isFormOpen && (
        <div className="modal-overlay" onClick={handleOverlayClick}>
          <div className="modal-content form-modal-size">
            <button className="close-modal-btn" onClick={() => setIsFormOpen(false)}>&times;</button>
            <iframe 
              src="https://forms.cloud.microsoft/Pages/ResponsePage.aspx?id=e5V92hEVQkqy9Xj4R_jIeknwnD8WXV5KrA11kYZu68tUQkdCSk5LVzBGUTJZOFBINUw5VElYME1WVy4u" 
              title="Sutra AI Signup"
              className="ms-form-frame" 
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}

      {isEmailModalOpen && (
        <div className="modal-overlay" onClick={handleOverlayClick}>
          <div className="modal-content email-modal-size">
            <button className="close-modal-btn" onClick={() => setIsEmailModalOpen(false)}>&times;</button>
            <div className="email-capture-container">
              {emailStatus === 'success' ? (
                <div className="email-success-msg">
                  <h2>Success!</h2>
                </div>
              ) : (
                <>
                  <h2>Stay Updated</h2>
                  <p>Enter email to unlock Wishlist.</p>
                  <form onSubmit={handleEmailSubmit} className="email-form">
                    <input type="email" placeholder="Email address" required value={email} onChange={(e) => setEmail(e.target.value)} className="email-input"/>
                    <button type="submit" className="action-btn form-btn full-width">Notify Me</button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </section>
  );
};

export default SutraPage;